// "Deal-Daten aktualisieren" fuer einen hochgeladenen oder bearbeiteten Vertrag:
// Nur die Stellen, an denen in der Vorlage ein Platzhalter steht, bekommen die
// Werte aus der Deal-Vorschau - der Vertragstext bleibt, wie er ist.
// Herausgeloest aus VertragsAutomationStudio.jsx am 08.10.2026, damit es sich
// testen laesst (dealDatenAktualisieren.test.js).

const PLACEHOLDER_TEXT_PATTERN = /\{\{\s*([^}]+?)\s*\}\}/g;

function containsPlaceholder(value) {
  return /\{\{\s*[^}]+?\s*\}\}/.test(String(value || ''));
}

function getContractSourceRoot(doc) {
  return doc?.getElementById?.('contract-source') || null;
}

function readEmbeddedEditorState(doc) {
  try {
    const raw = doc?.getElementById?.('enpower-editor-state')?.textContent || '';
    return raw ? JSON.parse(raw) : null;
  } catch (_error) {
    return null;
  }
}

function hydrateSourceEditorIdsFromEmbeddedState(doc) {
  const source = getContractSourceRoot(doc);
  if (!source) return;
  const state = readEmbeddedEditorState(doc);
  const order = Array.isArray(state?.order)
    ? state.order
    : (Array.isArray(state?.blockOrder) ? state.blockOrder : []);
  if (order.length === 0) return;
  const children = Array.from(source.children || []);
  order.forEach((id, index) => {
    const child = children[index];
    const normalizedId = String(id || '').trim();
    if (child && normalizedId && !child.getAttribute('data-editor-id')) {
      child.setAttribute('data-editor-id', normalizedId);
    }
  });
}

function normalizeMappingMatchText(value) {
  return String(value || '')
    .replace(PLACEHOLDER_TEXT_PATTERN, ' ')
    .replace(/\u00a0/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

function getPlaceholderStaticPartsForMatch(node) {
  return String(node?.textContent || '')
    .split(PLACEHOLDER_TEXT_PATTERN)
    .filter((_, index) => index % 2 === 0)
    .map(normalizeMappingMatchText)
    .filter((part) => part.length >= 4);
}

function getDirectClassName(node) {
  return String(node?.getAttribute?.('class') || '').replace(/\s+/g, ' ').trim();
}

function scoreTemplateCurrentBlockMatch(templateNode, currentNode) {
  if (!templateNode || !currentNode) return 0;
  if (!containsPlaceholder(templateNode.outerHTML || templateNode.textContent || '')) return 0;

  let score = 0;
  if (templateNode.tagName === currentNode.tagName) score += 8;
  if (getDirectClassName(templateNode) && getDirectClassName(templateNode) === getDirectClassName(currentNode)) score += 6;

  const currentText = normalizeMappingMatchText(currentNode.textContent || '');
  const staticParts = getPlaceholderStaticPartsForMatch(templateNode);
  let matchedParts = 0;
  staticParts.forEach((part) => {
    if (currentText.includes(part)) {
      matchedParts += 1;
      score += Math.min(18, 4 + Math.floor(part.length / 12));
    }
  });

  if (matchedParts === 0) return 0;
  return score;
}

function mirrorMissingEditorIdsFromCurrent(currentDoc, templateDoc, generatedDoc) {
  const currentSource = getContractSourceRoot(currentDoc);
  const templateSource = getContractSourceRoot(templateDoc);
  const generatedSource = getContractSourceRoot(generatedDoc);
  if (!currentSource || !templateSource || !generatedSource) return;

  const currentCandidates = Array.from(currentSource.children || [])
    .filter((node) => node.getAttribute?.('data-editor-id'));
  // Kennungen, die die Vorlage schon traegt, sind vergeben (Pruefung 08.10.2026):
  // Beim Grossspeicher bekam sonst die Flurstueck-Tabelle die Kennung der
  // Vollmacht-Tabelle, und deren Werte rutschten eine Zeile weiter.
  const usedCurrentIds = new Set(Array.from(templateSource.querySelectorAll('[data-editor-id]'))
    .map((node) => node.getAttribute('data-editor-id')));
  const templateChildren = Array.from(templateSource.children || []);
  const generatedChildren = Array.from(generatedSource.children || []);

  templateChildren.forEach((templateNode, index) => {
    if (templateNode.getAttribute?.('data-editor-id')) return;
    if (!containsPlaceholder(templateNode.outerHTML || templateNode.textContent || '')) return;

    let best = null;
    let bestScore = 0;
    currentCandidates.forEach((currentNode) => {
      const id = currentNode.getAttribute('data-editor-id');
      if (!id || usedCurrentIds.has(id)) return;
      const score = scoreTemplateCurrentBlockMatch(templateNode, currentNode);
      if (score > bestScore) {
        best = currentNode;
        bestScore = score;
      }
    });

    if (!best || bestScore < 16) return;
    const id = best.getAttribute('data-editor-id');
    templateNode.setAttribute('data-editor-id', id);
    const generatedNode = generatedChildren[index];
    if (generatedNode && !generatedNode.getAttribute?.('data-editor-id')) {
      generatedNode.setAttribute('data-editor-id', id);
    }
    usedCurrentIds.add(id);
  });
}

function findEditorBlockById(root, id) {
  if (!root || !id) return null;
  return Array.from(root.querySelectorAll('[data-editor-id]')).find((node) => (
    node.getAttribute('data-editor-id') === id
  )) || null;
}

function getClosestEditorBlock(node, sourceRoot) {
  let current = node?.nodeType === 1 ? node : node?.parentElement;
  while (current && current !== sourceRoot) {
    if (current.getAttribute?.('data-editor-id')) return current;
    current = current.parentElement;
  }
  return current?.getAttribute?.('data-editor-id') ? current : null;
}

function getChildNodePath(parent, node) {
  const path = [];
  let current = node;
  while (current && current !== parent) {
    const owner = current.parentNode;
    if (!owner) return null;
    path.unshift(Array.prototype.indexOf.call(owner.childNodes || [], current));
    current = owner;
  }
  return current === parent ? path : null;
}

function getNodeByChildNodePath(parent, path) {
  let current = parent;
  for (const index of path || []) {
    if (!current || !current.childNodes || index < 0 || index >= current.childNodes.length) return null;
    current = current.childNodes[index];
  }
  return current || null;
}

function walkNodes(root, callback) {
  if (!root) return;
  callback(root);
  Array.from(root.childNodes || []).forEach((child) => walkNodes(child, callback));
}

function parsePlaceholderTemplateText(templateText) {
  const placeholders = [];
  const staticParts = [];
  let lastIndex = 0;
  PLACEHOLDER_TEXT_PATTERN.lastIndex = 0;
  let match = PLACEHOLDER_TEXT_PATTERN.exec(templateText);
  while (match) {
    staticParts.push(templateText.slice(lastIndex, match.index));
    placeholders.push(String(match[1] || '').trim());
    lastIndex = match.index + match[0].length;
    match = PLACEHOLDER_TEXT_PATTERN.exec(templateText);
  }
  staticParts.push(templateText.slice(lastIndex));
  return { placeholders, staticParts };
}

function extractGeneratedPlaceholderValues(templateText, generatedText) {
  const parsed = parsePlaceholderTemplateText(templateText);
  if (parsed.placeholders.length === 0) return [];
  if (parsed.staticParts.every((part) => part === '')) return [generatedText];

  const values = [];
  let cursor = 0;
  for (let index = 0; index < parsed.placeholders.length; index += 1) {
    const before = parsed.staticParts[index] || '';
    if (before) {
      const beforeIndex = generatedText.indexOf(before, cursor);
      if (beforeIndex < 0) return null;
      cursor = beforeIndex + before.length;
    }

    const after = parsed.staticParts[index + 1] || '';
    if (after) {
      const afterIndex = generatedText.indexOf(after, cursor);
      if (afterIndex < 0) return null;
      values.push(generatedText.slice(cursor, afterIndex));
      cursor = afterIndex;
    } else {
      values.push(generatedText.slice(cursor));
      cursor = generatedText.length;
    }
  }
  return values;
}

function applyGeneratedPlaceholderValuesToCurrentText(templateText, currentText, generatedText) {
  if (!containsPlaceholder(templateText)) return { changed: false, value: currentText };
  const parsed = parsePlaceholderTemplateText(templateText);
  const values = extractGeneratedPlaceholderValues(templateText, generatedText);
  if (!values || values.length !== parsed.placeholders.length) return { changed: false, skipped: true, value: currentText };

  if (containsPlaceholder(currentText)) {
    // Namensbasiert ersetzen: So ueberleben eigene Textaenderungen im selben Knoten,
    // und ein einzelner unaufloesbarer Platzhalter blockiert nicht den ganzen Knoten.
    const wertNachName = {};
    parsed.placeholders.forEach((name, index) => {
      if (!(name in wertNachName)) wertNachName[name] = values[index];
    });
    const next = currentText.replace(PLACEHOLDER_TEXT_PATTERN, (token) => {
      const name = String(token).replace(/^\{\{\s*|\s*\}\}$/g, '');
      const wert = wertNachName[name];
      if (wert == null || containsPlaceholder(wert)) return token;
      return wert;
    });
    return { changed: next !== currentText, value: next };
  }

  if (values.some((value) => containsPlaceholder(value))) return { changed: false, skipped: true, value: currentText };

  if (parsed.staticParts.every((part) => part === '')) {
    return { changed: generatedText !== currentText, value: generatedText };
  }

  let cursor = 0;
  let nextText = '';
  for (let index = 0; index < values.length; index += 1) {
    const before = parsed.staticParts[index] || '';
    if (before) {
      const beforeIndex = currentText.indexOf(before, cursor);
      if (beforeIndex < 0) return { changed: false, skipped: true, value: currentText };
      const valueStart = beforeIndex + before.length;
      nextText += currentText.slice(cursor, valueStart);
      cursor = valueStart;
    }

    const after = parsed.staticParts[index + 1] || '';
    let valueEnd = currentText.length;
    if (after) {
      valueEnd = currentText.indexOf(after, cursor);
      if (valueEnd < 0) return { changed: false, skipped: true, value: currentText };
    }
    nextText += values[index] ?? '';
    cursor = valueEnd;
  }
  nextText += currentText.slice(cursor);
  return { changed: nextText !== currentText, value: nextText };
}

// ---------------------------------------------------------------------------
// Zuordnung ueber einen eindeutigen Anker (seit 08.10.2026).
//
// Zu einem Platzhalter der Vorlage wird das Element im hochgeladenen Vertrag
// gesucht: ueber den naechsten Vorfahren, der in beiden Dokumenten genau
// einmal vorkommt (Block-Kennung oder Tag/Id/Klassen, etwa ".party-grid"),
// und von dort ueber denselben Elementpfad (gleiche Tags). Ersetzt wird nur,
// was sicher passt:
//   - steht der Platzhalter allein im Element ("<p>{{Name}}</p>"), muss ein
//     fester Nachbar im selben Abstand gleich lauten (auf dem Deckblatt
//     "Vertragspartner / Grundstückseigentümer"), ausser das Element ist
//     selbst der Anker;
//   - steht Text daneben ("Vor- und Nachname: {{Name}}"), muss dieser Text im
//     hochgeladenen Vertrag an derselben Stelle stehen.
// Sonst bleibt die Stelle, wie sie ist ("unsicher").
// ---------------------------------------------------------------------------

function istLageplanBild(el) {
  return el?.tagName === 'IMG' && /lageplan/i.test(el.getAttribute('alt') || '');
}

// Leer, Platzhalter, Bitrix-Pfad (braucht Anmeldung) oder die Ersatzgrafik.
function istKaputteLageplanQuelle(src) {
  return !src || containsPlaceholder(src) || /show_file\.php|^\/bitrix\//i.test(src)
    || /^data:image\/svg\+xml;base64,PHN2ZyB4bWxucz0iaHR0cDov/i.test(src);
}

function istEchtesLageplanBild(src) {
  return !istKaputteLageplanQuelle(src) && (/^data:image\//i.test(src) || /^https?:\/\//i.test(src));
}

function normText(value) {
  // \s erfasst auch das geschuetzte Leerzeichen (U+00A0).
  return String(value || '').replace(/\s+/g, ' ').trim();
}

function klassen(el) {
  return String(el?.getAttribute?.('class') || '').split(/\s+/).filter(Boolean).sort().join('.');
}

function signatur(el) {
  return `${el.tagName}#${el.getAttribute('id') || ''}.${klassen(el)}`;
}

function gleicheElemente(doc, el) {
  const sig = signatur(el);
  return Array.from(doc.body?.getElementsByTagName(el.tagName) || []).filter((n) => signatur(n) === sig);
}

function mitBlockKennung(doc, id) {
  return Array.from(doc.querySelectorAll('[data-editor-id]')).filter((n) => n.getAttribute('data-editor-id') === id);
}

/** Der naechste Vorfahr (oder das Element selbst), der in Vorlage und Ziel genau einmal vorkommt. */
function findeAnker(el, templateDoc, zielDoc) {
  let kandidat = el;
  while (kandidat && kandidat !== templateDoc.body) {
    const id = kandidat.getAttribute?.('data-editor-id');
    if (id) {
      const imZiel = mitBlockKennung(zielDoc, id);
      if (imZiel.length === 1 && mitBlockKennung(templateDoc, id).length === 1) return { vorlage: kandidat, ziel: imZiel[0] };
    }
    if (klassen(kandidat) || kandidat.getAttribute('id')) {
      const inVorlage = gleicheElemente(templateDoc, kandidat);
      const imZiel = gleicheElemente(zielDoc, kandidat);
      if (inVorlage.length === 1 && imZiel.length === 1) return { vorlage: kandidat, ziel: imZiel[0] };
    }
    kandidat = kandidat.parentElement;
  }
  return null;
}

function elementPfad(von, bis) {
  const pfad = [];
  let knoten = bis;
  while (knoten && knoten !== von) {
    const eltern = knoten.parentElement;
    if (!eltern) return null;
    pfad.unshift({ index: Array.prototype.indexOf.call(eltern.children, knoten), tag: knoten.tagName });
    knoten = eltern;
  }
  return knoten === von ? pfad : null;
}

function folgeElementPfad(von, pfad) {
  let knoten = von;
  for (const schritt of pfad || []) {
    const kind = knoten?.children?.[schritt.index];
    if (!kind || kind.tagName !== schritt.tag) return null;
    knoten = kind;
  }
  return knoten;
}

/**
 * Die naechsten festen (platzhalterfreien) Nachbarn - davor UND danach -
 * lauten im Ziel im selben Abstand gleich. Beide, wo es sie gibt (Pruefung
 * 08.10.2026): Fehlte im hochgeladenen Deckblatt die Zeile "Firma", rutschten
 * sonst alle Werte eine Zeile weiter, und der letzte ueberschrieb "- im
 * folgenden Grundstückseigentümer genannt -". Die vom Werkzeug eingesetzte
 * Liste weiterer Eigentuemer zaehlt dabei nicht mit.
 */
function nachbarStimmt(elT, elZ) {
  const geschwisterT = Array.from(elT.parentElement?.children || []);
  const geschwisterZ = Array.from(elZ.parentElement?.children || [])
    .filter((el) => el === elZ || !el.hasAttribute('data-additional-owners'));
  const iT = geschwisterT.indexOf(elT);
  const iZ = geschwisterZ.indexOf(elZ);
  let geprueft = 0;
  for (const richtung of [-1, 1]) {
    for (let abstand = 1; iT + richtung * abstand >= 0 && iT + richtung * abstand < geschwisterT.length; abstand += 1) {
      const t = geschwisterT[iT + richtung * abstand];
      const fest = normText(t.textContent);
      if (!fest || containsPlaceholder(t.innerHTML)) continue;
      const z = geschwisterZ[iZ + richtung * abstand];
      if (!z || normText(z.textContent) !== fest) return false;
      geprueft += 1;
      break;
    }
  }
  return geprueft > 0;
}

function zielElement(elT, templateDoc, zielDoc) {
  const anker = findeAnker(elT, templateDoc, zielDoc);
  if (!anker) return null;
  const el = folgeElementPfad(anker.ziel, elementPfad(anker.vorlage, elT));
  return el ? { el, istAnker: anker.vorlage === elT } : null;
}

/** @returns {'geaendert'|'gleich'|'unsicher'} */
function setzeUeberAnker(templateNode, { templateDoc, currentDoc, generatedDoc }) {
  const elT = templateNode.parentElement;
  if (!elT) return 'unsicher';
  const z = zielElement(elT, templateDoc, currentDoc);
  const g = zielElement(elT, templateDoc, generatedDoc);
  if (!z || !g || klassen(z.el) !== klassen(elT)) return 'unsicher';

  const knotenT = Array.from(elT.childNodes);
  const fest = normText(parsePlaceholderTemplateText(templateNode.nodeValue || '').staticParts.join(''));
  // Ohne festen Text daneben ist nur der Nachbar ein Beleg fuer die richtige Stelle.
  if (fest.length < 4 && !z.istAnker && !nachbarStimmt(elT, z.el)) return 'unsicher';

  if (knotenT.length === 1 && !fest) {
    // Der Platzhalter allein im Element: der ganze Text wird ersetzt - auch wenn er leer war.
    if (z.el.children.length || g.el.children.length) return 'unsicher';
    const neu = g.el.textContent;
    if (containsPlaceholder(neu)) return 'unsicher';
    if (z.el.textContent === neu) return 'gleich';
    z.el.textContent = neu;
    return 'geaendert';
  }

  const index = knotenT.indexOf(templateNode);
  if (z.el.childNodes.length !== knotenT.length || g.el.childNodes.length !== knotenT.length) return 'unsicher';
  const knotenZ = z.el.childNodes[index];
  const knotenG = g.el.childNodes[index];
  if (knotenZ?.nodeType !== 3 || knotenG?.nodeType !== 3) return 'unsicher';
  const angewandt = applyGeneratedPlaceholderValuesToCurrentText(templateNode.nodeValue || '', knotenZ.nodeValue || '', knotenG.nodeValue || '');
  if (angewandt.skipped) return 'unsicher';
  if (!angewandt.changed) return 'gleich';
  knotenZ.nodeValue = angewandt.value;
  return 'geaendert';
}

export function refreshMappedFieldsInEditedHtml(currentHtml, templateHtml, generatedHtml) {
  if (!currentHtml || !templateHtml || !generatedHtml) {
    throw new Error('Aktueller Vertrag, Vorlage oder Deal-Vorschau fehlt.');
  }

  const parser = new DOMParser();
  const currentDoc = parser.parseFromString(currentHtml, 'text/html');
  const templateDoc = parser.parseFromString(templateHtml, 'text/html');
  const generatedDoc = parser.parseFromString(generatedHtml, 'text/html');
  hydrateSourceEditorIdsFromEmbeddedState(currentDoc);
  hydrateSourceEditorIdsFromEmbeddedState(templateDoc);
  hydrateSourceEditorIdsFromEmbeddedState(generatedDoc);
  mirrorMissingEditorIdsFromCurrent(currentDoc, templateDoc, generatedDoc);
  const currentSource = getContractSourceRoot(currentDoc);
  const templateSource = getContractSourceRoot(templateDoc);
  const generatedSource = getContractSourceRoot(generatedDoc);
  if (!currentSource || !templateSource || !generatedSource) {
    throw new Error('Vertragsstruktur (#contract-source) konnte nicht gelesen werden.');
  }

  // Gerenderte Seiten sind Kopien - fuer die Zuordnung zaehlt nur die Quelle.
  [currentDoc, templateDoc, generatedDoc].forEach((doc) => {
    const seiten = doc.getElementById('contract-pages');
    if (seiten) seiten.innerHTML = '';
  });

  let updated = 0;
  let skipped = 0;
  // Schon aktuell - damit die Oberflaeche "bereits aktuell" sagen kann statt
  // "keine Mapping-Stellen aktualisiert, bitte pruefen".
  let unchanged = 0;
  // Was ueber die Block-Kennung nicht zuzuordnen war - zweiter Versuch unten.
  const offen = [];

  walkNodes(templateSource, (templateNode) => {
    if (templateNode.nodeType !== 3 || !containsPlaceholder(templateNode.nodeValue)) return;
    const templateBlock = getClosestEditorBlock(templateNode, templateSource);
    const blockId = templateBlock?.getAttribute?.('data-editor-id') || '';
    if (!blockId) {
      offen.push(templateNode);
      return;
    }

    const path = getChildNodePath(templateBlock, templateNode);
    const currentBlock = findEditorBlockById(currentSource, blockId);
    const generatedBlock = findEditorBlockById(generatedSource, blockId);
    const currentNode = getNodeByChildNodePath(currentBlock, path);
    const generatedNode = getNodeByChildNodePath(generatedBlock, path);
    if (!currentNode || !generatedNode || currentNode.nodeType !== 3 || generatedNode.nodeType !== 3) {
      offen.push(templateNode);
      return;
    }

    const applied = applyGeneratedPlaceholderValuesToCurrentText(
      templateNode.nodeValue || '',
      currentNode.nodeValue || '',
      generatedNode.nodeValue || '',
    );
    if (applied.skipped) {
      offen.push(templateNode);
      return;
    }
    if (applied.changed) {
      currentNode.nodeValue = applied.value;
      updated += 1;
    } else {
      unchanged += 1;
    }
  });

  // Zweiter Versuch (seit 08.10.2026): ueber einen eindeutigen Anker in der
  // Naehe - fuer Stellen ohne Block-Kennung und fuer alles ausserhalb von
  // #contract-source, vor allem das Deckblatt mit den Kundendaten. Vorher
  // wurden dort nur noch offene {{Platzhalter}} ersetzt; in einem
  // hochgeladenen, ausgefuellten Vertrag blieben die alten Kundendaten stehen.
  walkNodes(templateDoc.body, (templateNode) => {
    if (templateNode.nodeType !== 3 || !containsPlaceholder(templateNode.nodeValue)) return;
    if (templateSource.contains(templateNode)) return;
    const eltern = templateNode.parentElement;
    if (eltern && /^(script|style|title)$/i.test(eltern.tagName)) return;
    offen.push(templateNode);
  });
  offen.forEach((templateNode) => {
    const ergebnis = setzeUeberAnker(templateNode, { templateDoc, currentDoc, generatedDoc });
    if (ergebnis === 'geaendert') updated += 1;
    else if (ergebnis === 'unsicher') skipped += 1;
    else unchanged += 1;
  });

  walkNodes(templateSource, (templateNode) => {
    if (templateNode.nodeType !== 1) return;
    // Das Lageplan-Bild regelt der eigene Schritt unten - hier koennte die
    // Ersatzgrafik ein vorhandenes Bild ueberschreiben.
    if (istLageplanBild(templateNode)) return;
    const attrs = Array.from(templateNode.attributes || []).filter((attr) => containsPlaceholder(attr.value));
    if (attrs.length === 0) return;

    const templateBlock = getClosestEditorBlock(templateNode, templateSource);
    const blockId = templateBlock?.getAttribute?.('data-editor-id') || '';
    const path = getChildNodePath(templateBlock, templateNode);
    const currentNode = getNodeByChildNodePath(findEditorBlockById(currentSource, blockId), path);
    const generatedNode = getNodeByChildNodePath(findEditorBlockById(generatedSource, blockId), path);
    if (!blockId || !currentNode || !generatedNode || currentNode.nodeType !== 1 || generatedNode.nodeType !== 1) {
      skipped += attrs.length;
      return;
    }

    attrs.forEach((attr) => {
      const nextValue = generatedNode.getAttribute(attr.name);
      if (nextValue == null || containsPlaceholder(nextValue)) {
        skipped += 1;
        return;
      }
      if (currentNode.getAttribute(attr.name) !== nextValue) {
        currentNode.setAttribute(attr.name, nextValue);
        updated += 1;
      }
    });
  });

  // Globale Namens-Ersetzung: Die Struktur-Durchlaeufe oben erfassen nur Bloecke in
  // #contract-source. Platzhalter AUSSERHALB davon (z. B. auf dem Deckblatt) blieben
  // bisher stehen. Hier werden Name->Wert-Paare aus Vorlage/Deal-Vorschau gesammelt
  // und alle uebrigen {{...}}-Tokens im gesamten Dokument ersetzt.
  const wertNachName = {};
  const merkeWert = (name, wert) => {
    const sauber = String(name || '').trim();
    if (!sauber || wert == null) return;
    if (containsPlaceholder(wert)) return;
    if (!(sauber in wertNachName)) wertNachName[sauber] = wert;
  };
  const sammlePaare = (templateText, generatedText) => {
    const parsed = parsePlaceholderTemplateText(templateText);
    const values = extractGeneratedPlaceholderValues(templateText, generatedText);
    if (!values || values.length !== parsed.placeholders.length) return;
    parsed.placeholders.forEach((name, index) => merkeWert(name, values[index]));
  };
  // Innerhalb von #contract-source: robuste Zuordnung ueber Block-Id + Pfad.
  walkNodes(templateSource, (templateNode) => {
    if (templateNode.nodeType !== 3 || !containsPlaceholder(templateNode.nodeValue)) return;
    const templateBlock = getClosestEditorBlock(templateNode, templateSource);
    const blockId = templateBlock?.getAttribute?.('data-editor-id') || '';
    if (!blockId) return;
    const generatedNode = getNodeByChildNodePath(
      findEditorBlockById(generatedSource, blockId),
      getChildNodePath(templateBlock, templateNode),
    );
    if (generatedNode && generatedNode.nodeType === 3) {
      sammlePaare(templateNode.nodeValue || '', generatedNode.nodeValue || '');
    }
  });
  // Ausserhalb von #contract-source (Deckblatt etc.): absolute Pfade sind stabil,
  // weil Vorlage und Vorschau aus demselben Dokument stammen.
  walkNodes(templateDoc.documentElement, (templateNode) => {
    if (templateSource.contains(templateNode)) return;
    if (templateNode.nodeType === 3 && containsPlaceholder(templateNode.nodeValue)) {
      const generatedNode = getNodeByChildNodePath(
        generatedDoc.documentElement,
        getChildNodePath(templateDoc.documentElement, templateNode),
      );
      if (generatedNode && generatedNode.nodeType === 3) {
        sammlePaare(templateNode.nodeValue || '', generatedNode.nodeValue || '');
      }
      return;
    }
    if (templateNode.nodeType !== 1) return;
    Array.from(templateNode.attributes || []).forEach((attr) => {
      const einzeln = String(attr.value || '').match(/^\s*\{\{\s*([^}]+?)\s*\}\}\s*$/);
      if (!einzeln) return;
      const generatedNode = getNodeByChildNodePath(
        generatedDoc.documentElement,
        getChildNodePath(templateDoc.documentElement, templateNode),
      );
      if (generatedNode && generatedNode.nodeType === 1) {
        merkeWert(einzeln[1], generatedNode.getAttribute(attr.name));
      }
    });
  });
  const ersetzeTokens = (text) => String(text).replace(PLACEHOLDER_TEXT_PATTERN, (token) => {
    const name = String(token).replace(/^\{\{\s*|\s*\}\}$/g, '').trim();
    const wert = wertNachName[name];
    return wert == null ? token : wert;
  });
  walkNodes(currentDoc.documentElement, (node) => {
    if (node.nodeType === 3) {
      const eltern = node.parentElement;
      if (eltern && /^(script|style)$/i.test(eltern.tagName)) return;
      if (!containsPlaceholder(node.nodeValue)) return;
      const neu = ersetzeTokens(node.nodeValue || '');
      if (neu !== node.nodeValue) {
        node.nodeValue = neu;
        updated += 1;
      }
      return;
    }
    if (node.nodeType !== 1) return;
    Array.from(node.attributes || []).forEach((attr) => {
      if (!containsPlaceholder(attr.value)) return;
      const neu = ersetzeTokens(attr.value);
      if (neu !== attr.value) {
        node.setAttribute(attr.name, neu);
        updated += 1;
      }
    });
  });

  // Lageplan: Ein echtes Bild aus dem Deal ersetzt immer das vorhandene - auch
  // ein aelteres echtes (seit 08.10.2026; vorher nur ein fehlendes oder
  // kaputtes). Die Ersatzgrafik ("Lageplan-Bild aus Bitrix wird hier
  // eingefuegt") kommt nur dorthin, wo kein brauchbares Bild steht.
  // Wurde das Bild frueher durch den "Lageplan folgt"-Hinweis ersetzt
  // (onerror-Span), gibt es kein img mehr - dann wird es wieder eingesetzt.
  const generatedLageplan = Array.from(generatedSource.querySelectorAll('img')).find(istLageplanBild);
  const neueQuelle = generatedLageplan?.getAttribute('src') || '';
  if (generatedLageplan && neueQuelle && !containsPlaceholder(neueQuelle)) {
    const aktuellesImg = Array.from(currentSource.querySelectorAll('img')).find(istLageplanBild);
    const alteQuelle = aktuellesImg?.getAttribute('src') || '';
    const neuIstEcht = istEchtesLageplanBild(neueQuelle);
    const ersatzFuerKaputt = /^data:image\//i.test(neueQuelle) && istKaputteLageplanQuelle(alteQuelle);
    if (aktuellesImg && alteQuelle !== neueQuelle && (neuIstEcht || ersatzFuerKaputt)) {
      aktuellesImg.setAttribute('src', neueQuelle);
      aktuellesImg.removeAttribute('data-f');
      updated += 1;
    } else if (!aktuellesImg && /^data:image\//i.test(neueQuelle)) {
      const leererHinweis = currentSource.querySelector('.anlage2-page-empty');
      if (leererHinweis) {
        leererHinweis.replaceWith(currentDoc.importNode(generatedLageplan, true));
        updated += 1;
      }
    }
  }

  const generatedPages = currentDoc.getElementById('contract-pages');
  if (generatedPages) generatedPages.innerHTML = '';

  return {
    html: '<!doctype html>\n' + currentDoc.documentElement.outerHTML,
    updated,
    skipped,
    unchanged,
  };
}
